import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import toast from 'react-hot-toast';
import { codingStudentService } from '@/services/coding/codingStudentService';
import type {
  StudentCodingProblemDetails,
  RunCodeResponse,
  SubmitCodeResponse,
} from '@/services/coding/codingStudentService';
import { useStudentTheme } from '@context/StudentThemeContext';
import {
  ArrowLeft,
  ArrowRight,
  Play,
  Send,
  RotateCcw,
  Copy,
  Check,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  FileCode2,
  History,
  Terminal,
  Maximize2,
  Minimize2,
  PartyPopper,
  Lock,
  ExternalLink,
} from 'lucide-react';
import { useRipple } from '@/hooks/useRipple';

interface CodingTaskPanelProps {
  problem: StudentCodingProblemDetails;
  problemNumber: number;
  totalProblems: number;
  isLastProblem: boolean;
  onNext: () => void;
  onPrevious: () => void;
}

const CodingTaskPanel: React.FC<CodingTaskPanelProps> = ({
  problem,
  problemNumber,
  totalProblems,
  isLastProblem,
  onNext,
  onPrevious,
}) => {
  const queryClient = useQueryClient();
  const { theme } = useStudentTheme();
  const ripple = useRipple();

  const [leftTab, setLeftTab] = useState<'description' | 'submissions'>('description');
  const [bottomTab, setBottomTab] = useState<'testcases' | 'results'>('testcases');
  const [activeCaseIndex, setActiveCaseIndex] = useState(0);

  const [sourceCode, setSourceCode] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Execution state
  const [runResult, setRunResult] = useState<RunCodeResponse | null>(null);
  const [submitResult, setSubmitResult] = useState<SubmitCodeResponse | null>(null);
  const [lastAction, setLastAction] = useState<'run' | 'submit' | null>(null);

  // Reset per-problem view state whenever the active problem changes
  useEffect(() => {
    setLeftTab('description');
    setBottomTab('testcases');
    setActiveCaseIndex(0);
    setRunResult(null);
    setSubmitResult(null);
    setLastAction(null);
  }, [problem.id]);

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Initialize code with starter code or last submission
  useEffect(() => {
    const draftKey = `coding_draft_${problem.id}`;
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      setSourceCode(savedDraft);
    } else if (problem.recent_submissions && problem.recent_submissions.length > 0) {
      setSourceCode(problem.recent_submissions[0].source_code);
    } else {
      setSourceCode(problem.starter_code);
    }
  }, [problem.id]);

  // Auto-save code draft
  useEffect(() => {
    if (problem?.id && sourceCode) {
      localStorage.setItem(`coding_draft_${problem.id}`, sourceCode);
    }
  }, [sourceCode, problem?.id]);

  // Run Code Mutation
  const runMutation = useMutation({
    mutationFn: (code: string) =>
      codingStudentService.runCode({
        problem_id: problem.id,
        source_code: code,
        language: problem.language,
      }),
    onSuccess: (res) => {
      setRunResult(res.data.data);
      setSubmitResult(null);
      setLastAction('run');
      setBottomTab('results');
      if (res.data.data.status === 'accepted') {
        toast.success('Public test cases passed!');
      } else {
        toast.error(`Run: ${res.data.data.status.replace('_', ' ').toUpperCase()}`);
      }
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to execute code');
    },
  });

  // Submit Code Mutation
  const submitMutation = useMutation({
    mutationFn: (code: string) =>
      codingStudentService.submitCode({
        problem_id: problem.id,
        source_code: code,
        language: problem.language,
      }),
    onSuccess: (res) => {
      setSubmitResult(res.data.data);
      setRunResult(null);
      setLastAction('submit');
      setBottomTab('results');
      queryClient.invalidateQueries({ queryKey: ['student-coding-problem', problem.id] });
      queryClient.invalidateQueries({ queryKey: ['student-coding-problems'] });

      if (res.data.data.status === 'accepted') {
        toast.success('Accepted! All test cases passed!');
      } else {
        toast.error(`Verdict: ${res.data.data.status.replace('_', ' ').toUpperCase()}`);
      }
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to submit code');
    },
  });

  const handleRun = () => {
    if (!sourceCode.trim()) {
      toast.error('Please write some code before running');
      return;
    }
    runMutation.mutate(sourceCode);
  };

  const handleSubmit = () => {
    if (!sourceCode.trim()) {
      toast.error('Please write some code before submitting');
      return;
    }
    submitMutation.mutate(sourceCode);
  };

  const handleResetCode = () => {
    if (window.confirm('Reset code back to the original starter code? Any unsaved changes will be lost.')) {
      setSourceCode(problem.starter_code);
      localStorage.removeItem(`coding_draft_${problem.id}`);
      toast.success('Code reset to template');
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(sourceCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Code copied to clipboard');
  };

  const isRunning = runMutation.isPending;
  const isSubmitting = submitMutation.isPending;
  const isExecuting = isRunning || isSubmitting;
  const hasSubmitted =
    (problem.recent_submissions && problem.recent_submissions.length > 0) ||
    (submitResult !== null && submitMutation.isSuccess);

  const publicTestCases = problem.public_test_cases || [];

  const getDifficultyPill = (diff: string) => {
    switch (diff?.toUpperCase()) {
      case 'EASY':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
            Easy
          </span>
        );
      case 'MEDIUM':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
            Medium
          </span>
        );
      case 'HARD':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
            Hard
          </span>
        );
      default:
        return <span>{diff}</span>;
    }
  };

  return (
    <div className={isFullscreen ? 'fixed inset-0 z-50 bg-slate-950 p-4 sm:p-6 text-white overflow-y-auto space-y-4' : 'space-y-4'}>
      {/* Top Header Banner: Problem title, tags, and actions */}
      <div className={`border rounded-2xl p-4 shadow-soft flex flex-wrap items-center justify-between gap-4 ${
        isFullscreen
          ? 'bg-slate-900 border-slate-800 text-white'
          : 'bg-white dark:bg-slate-900 border-border'
      }`}>
        {/* Left: Problem info */}
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-primary/10 text-primary rounded-xl shrink-0">
            <FileCode2 size={22} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-mono font-bold text-slate-400">
                Problem {problemNumber} of {totalProblems}
              </span>
              {getDifficultyPill(problem.difficulty)}
              {problem.student_status === 'SOLVED' && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300">
                  <CheckCircle2 size={11} /> Solved
                </span>
              )}
            </div>
            <h1 className={`text-lg font-bold mt-0.5 ${isFullscreen ? 'text-white' : 'text-navy dark:text-white'}`}>
              {problem.title}
            </h1>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {problem.problem_type === 'NOTEBOOK' && (
            <a
              href={`/student/notebook?taskId=${problem.id}`}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs font-bold transition-all shadow-sm"
            >
              <ExternalLink size={14} />
              Open in Python Lab (Colab)
            </a>
          )}

          {/* Copy Code */}
          <button
            type="button"
            onClick={handleCopyCode}
            onPointerDown={ripple.onPointerDown}
            title="Copy Code"
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-all cursor-pointer"
          >
            {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
          </button>

          {/* Reset Code */}
          <button
            type="button"
            onClick={handleResetCode}
            onPointerDown={ripple.onPointerDown}
            title="Reset to Starter Code"
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-all cursor-pointer"
          >
            <RotateCcw size={16} />
          </button>

          {/* Toggle Fullscreen */}
          <button
            type="button"
            onClick={() => setIsFullscreen(!isFullscreen)}
            onPointerDown={ripple.onPointerDown}
            title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen Editor'}
            className={`p-2 rounded-xl transition-all cursor-pointer flex items-center gap-1.5 ${
              isFullscreen
                ? 'bg-primary text-white hover:bg-primary/90 font-bold text-xs px-3 shadow-sm'
                : 'text-slate-500 hover:text-navy hover:bg-gray-100 dark:hover:bg-slate-800'
            }`}
          >
            {isFullscreen ? (
              <>
                <Minimize2 size={16} />
                <span>Exit Fullscreen (Esc)</span>
              </>
            ) : (
              <Maximize2 size={16} />
            )}
          </button>
        </div>
      </div>

      {/* Main Grid: Description (5 cols) & Editor / Test Results (7 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* LEFT COLUMN: Problem Description & Submissions History (5 cols) */}
        <div className="lg:col-span-5 bg-white dark:bg-slate-900 border border-border rounded-2xl overflow-hidden shadow-soft flex flex-col min-h-[560px]">
          {/* Left Tabs Bar */}
          <div className="flex border-b border-border bg-gray-50/60 dark:bg-slate-800/40 p-1.5 gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setLeftTab('description')}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                leftTab === 'description'
                  ? 'bg-white dark:bg-slate-900 text-primary border-t border-x border-border shadow-xs'
                  : 'text-slate-500 hover:text-navy dark:hover:text-white'
              }`}
            >
              <FileCode2 size={14} />
              Description
            </button>
            <button
              type="button"
              onClick={() => setLeftTab('submissions')}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                leftTab === 'submissions'
                  ? 'bg-white dark:bg-slate-900 text-primary border-t border-x border-border shadow-xs'
                  : 'text-slate-500 hover:text-navy dark:hover:text-white'
              }`}
            >
              <History size={14} />
              Submissions ({problem.recent_submissions?.length || 0})
            </button>
          </div>

          {/* Left Tab Content */}
          <div className="p-5 overflow-y-auto flex-1 space-y-4 max-h-[640px]">
            {leftTab === 'description' ? (
              <div className="space-y-4 text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                {/* Description Body */}
                <div className="prose prose-xs dark:prose-invert max-w-none">
                  <div className="whitespace-pre-wrap font-sans">{problem.description}</div>
                </div>

                {/* Input Format */}
                {problem.input_format && (
                  <div className="space-y-1 pt-2 border-t border-gray-100 dark:border-slate-800">
                    <span className="font-bold text-navy dark:text-white block">Input Format</span>
                    <p className="font-mono text-[11px] bg-gray-50 dark:bg-slate-800/60 p-2 rounded-lg text-slate-600 dark:text-slate-300">
                      {problem.input_format}
                    </p>
                  </div>
                )}

                {/* Output Format */}
                {problem.output_format && (
                  <div className="space-y-1 pt-2 border-t border-gray-100 dark:border-slate-800">
                    <span className="font-bold text-navy dark:text-white block">Output Format</span>
                    <p className="font-mono text-[11px] bg-gray-50 dark:bg-slate-800/60 p-2 rounded-lg text-slate-600 dark:text-slate-300">
                      {problem.output_format}
                    </p>
                  </div>
                )}

                {/* Constraints */}
                {problem.constraints && (
                  <div className="space-y-1 pt-2 border-t border-gray-100 dark:border-slate-800">
                    <span className="font-bold text-navy dark:text-white block">Constraints</span>
                    <pre className="font-mono text-[11px] bg-gray-50 dark:bg-slate-800/60 p-2.5 rounded-lg text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                      {problem.constraints}
                    </pre>
                  </div>
                )}

                {/* Function signature info */}
                {problem.problem_type === 'FUNCTION' && problem.function_name && (
                  <div className="space-y-1 pt-2 border-t border-gray-100 dark:border-slate-800">
                    <span className="font-bold text-navy dark:text-white block">Required Function</span>
                    <code className="block font-mono text-[11px] bg-primary/10 text-primary p-2 rounded-lg">
                      def {problem.function_name}(...):
                    </code>
                  </div>
                )}
              </div>
            ) : (
              /* Submissions History Tab */
              <div className="space-y-3">
                {problem.recent_submissions?.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 text-xs">
                    <Clock size={32} className="mx-auto mb-2 opacity-50" />
                    No submissions yet. Write code and hit "Submit" to see your attempts here.
                  </div>
                ) : (
                  problem.recent_submissions.map((sub) => (
                    <div
                      key={sub.id}
                      className="p-3 bg-gray-50 dark:bg-slate-800/60 border border-border rounded-xl space-y-1.5"
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${
                            sub.status === 'ACCEPTED'
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-400'
                              : 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950/60 dark:text-rose-400'
                          }`}
                        >
                          {sub.status}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {new Date(sub.submitted_at).toLocaleString()}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-xs pt-1">
                        <span className="text-slate-600 dark:text-slate-300">
                          Tests: <b>{sub.passed_tests}/{sub.total_tests}</b>
                          {sub.execution_time_ms != null && `  ${Math.round(sub.execution_time_ms)}ms`}
                        </span>

                        <button
                          type="button"
                          onClick={() => {
                            setSourceCode(sub.source_code);
                            toast.success('Loaded past submission code into editor');
                          }}
                          className="text-primary font-bold text-[11px] hover:underline cursor-pointer"
                        >
                          Restore Code
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Monaco Editor & Test Cases Panel (7 cols) */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          {/* Top Monaco Editor container */}
          <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden shadow-soft flex flex-col h-[420px]">
            {/* Editor Top Bar */}
            <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-800 text-slate-400 text-xs">
              <span className="font-mono text-emerald-400 font-bold flex items-center gap-1.5">
                <Terminal size={14} />
                solution.py
              </span>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-slate-500 hidden sm:inline">
                  Ctrl+Enter to Run &bull; Ctrl+Shift+Enter to Submit
                </span>
                {isFullscreen && (
                  <button
                    type="button"
                    onClick={() => setIsFullscreen(false)}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-400 hover:text-amber-300 transition-colors cursor-pointer"
                  >
                    <Minimize2 size={12} />
                    Exit Fullscreen (Esc)
                  </button>
                )}
              </div>
            </div>

            {/* Monaco Component */}
            <div className="flex-1">
              <Editor
                height="100%"
                language="python"
                value={sourceCode}
                onChange={(val) => setSourceCode(val || '')}
                theme={theme === 'dark' ? 'vs-dark' : 'light'}
                options={{
                  fontSize: 13,
                  lineNumbers: 'on',
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  tabSize: 4,
                  scrollbar: { alwaysConsumeMouseWheel: false },
                  fontFamily: "'Fira Code', 'Cascadia Code', Consolas, monospace",
                }}
              />
            </div>
          </div>

          {/* Bottom Execution Panel: Run / Submit Buttons & Test Cases Tabs */}
          <div className="bg-white dark:bg-slate-900 border border-border rounded-2xl shadow-soft overflow-hidden flex flex-col">
            {/* Action Bar */}
            <div className="p-3 border-b border-border bg-gray-50/60 dark:bg-slate-800/40 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {/* Tab switchers: Test Cases vs Results */}
                <button
                  type="button"
                  onClick={() => setBottomTab('testcases')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    bottomTab === 'testcases'
                      ? 'bg-white dark:bg-slate-900 text-primary shadow-xs'
                      : 'text-slate-500 hover:text-navy dark:hover:text-white'
                  }`}
                >
                  Sample Test Cases ({publicTestCases.length})
                </button>
                <button
                  type="button"
                  onClick={() => setBottomTab('results')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                    bottomTab === 'results'
                      ? 'bg-white dark:bg-slate-900 text-primary shadow-xs'
                      : 'text-slate-500 hover:text-navy dark:hover:text-white'
                  }`}
                >
                  <span>Test Results</span>
                  {submitResult && (
                    <span
                      className={`w-2 h-2 rounded-full ${
                        submitResult.status === 'accepted' ? 'bg-emerald-500' : 'bg-rose-500'
                      }`}
                    />
                  )}
                </button>
              </div>

              {/* Submission status hint */}
              {hasSubmitted && (
                <span className="text-[10px] text-amber-700 dark:text-amber-300 font-semibold px-2.5 py-1 bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800 rounded-lg hidden md:inline-flex items-center gap-1">
                  <Lock size={10} />
                  Submission submitted &amp; locked (contact admin to revoke)
                </span>
              )}

              {/* Run and Submit Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={isExecuting}
                  onClick={handleRun}
                  onPointerDown={ripple.onPointerDown}
                  className="ripple-trigger inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
                >
                  {isRunning ? (
                    <div className="animate-spin h-3.5 w-3.5 border-2 border-primary border-t-transparent rounded-full" />
                  ) : (
                    <Play size={13} className="text-emerald-600 fill-emerald-600" />
                  )}
                  Run Code
                </button>

                {hasSubmitted ? (
                  <button
                    type="button"
                    disabled={true}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 rounded-xl text-xs font-bold cursor-not-allowed select-none border border-gray-300 dark:border-slate-700"
                    title="You have already submitted this task. Multiple submissions are disabled unless revoked by an admin."
                  >
                    <Lock size={13} />
                    Submitted (Locked)
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={isExecuting}
                    onClick={handleSubmit}
                    onPointerDown={ripple.onPointerDown}
                    className="ripple-trigger inline-flex items-center gap-1.5 px-5 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-black transition-all cursor-pointer shadow-sm disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <Send size={13} />
                    )}
                    Submit
                  </button>
                )}
              </div>
            </div>

            {/* Bottom Content Area */}
            <div className="p-4 min-h-[160px] text-xs">
              {bottomTab === 'testcases' ? (
                /* Tab: Sample Test Cases */
                <div className="space-y-3">
                  {/* Case Pill Selector */}
                  <div className="flex items-center gap-2 overflow-x-auto pb-1">
                    {publicTestCases.map((tc, idx) => (
                      <button
                        key={tc.id}
                        type="button"
                        onClick={() => setActiveCaseIndex(idx)}
                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          activeCaseIndex === idx
                            ? 'bg-blue-bg text-primary border border-primary/30'
                            : 'bg-gray-100 dark:bg-slate-800 text-slate-500 hover:text-navy'
                        }`}
                      >
                        Case {idx + 1}
                      </button>
                    ))}
                  </div>

                  {/* Active Test Case Detail */}
                  {publicTestCases[activeCaseIndex] && (
                    <div className="space-y-2 pt-1">
                      <div>
                        <span className="text-[10px] text-slate-400 font-bold block mb-1">
                          Input
                        </span>
                        <pre className="p-3 bg-slate-950 text-slate-200 font-mono text-xs rounded-xl border border-slate-800 overflow-x-auto">
                          {publicTestCases[activeCaseIndex].input || '<No Input>'}
                        </pre>
                      </div>

                      <div>
                        <span className="text-[10px] text-slate-400 font-bold block mb-1">
                          Expected Output
                        </span>
                        <pre className="p-3 bg-slate-950 text-emerald-400 font-mono text-xs rounded-xl border border-slate-800 overflow-x-auto">
                          {publicTestCases[activeCaseIndex].expected_output}
                        </pre>
                      </div>

                      {publicTestCases[activeCaseIndex].explanation && (
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 italic">
                          Note: {publicTestCases[activeCaseIndex].explanation}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Tab: Test Results */
                <div className="space-y-4">
                  {/* Verdict header banner */}
                  {lastAction === 'submit' && submitResult && (
                    <div
                      className={`p-3.5 rounded-xl border flex items-center justify-between ${
                        submitResult.status === 'accepted'
                          ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                          : 'bg-rose-50 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {submitResult.status === 'accepted' ? (
                          <CheckCircle2 size={20} className="text-emerald-600 shrink-0" />
                        ) : (
                          <XCircle size={20} className="text-rose-600 shrink-0" />
                        )}
                        <div>
                          <div className="font-black text-sm">
                            {submitResult.status === 'accepted'
                              ? 'Accepted!'
                              : `Verdict: ${submitResult.status.replace('_', ' ').toUpperCase()}`}
                          </div>
                          <div className="text-[11px] opacity-80">
                            Passed {submitResult.passed} of {submitResult.total} total test cases (including hidden tests)
                          </div>
                        </div>
                      </div>

                      {submitResult.execution_time_ms != null && (
                        <span className="text-[11px] font-mono font-bold">
                          {Math.round(submitResult.execution_time_ms)}ms
                        </span>
                      )}
                    </div>
                  )}

                  {lastAction === 'run' && runResult && (
                    <div
                      className={`p-3.5 rounded-xl border flex items-center justify-between ${
                        runResult.status === 'accepted'
                          ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                          : 'bg-rose-50 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {runResult.status === 'accepted' ? (
                          <CheckCircle2 size={20} className="text-emerald-600 shrink-0" />
                        ) : (
                          <AlertTriangle size={20} className="text-rose-600 shrink-0" />
                        )}
                        <div>
                          <div className="font-black text-sm">
                            {runResult.status === 'accepted' ? 'Public Tests Passed' : 'Tests Failed'}
                          </div>
                          <div className="text-[11px] opacity-80">
                            Passed {runResult.passed} of {runResult.total} sample test cases
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Individual test case results breakdown */}
                  {((lastAction === 'submit' ? submitResult?.results : runResult?.results) || []).map(
                    (res: any, idx: number) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-xl border space-y-2 ${
                          res.status === 'passed'
                            ? 'border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/30 dark:bg-emerald-950/20'
                            : 'border-rose-200 dark:border-rose-900/60 bg-rose-50/30 dark:bg-rose-950/20'
                        }`}
                      >
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold flex items-center gap-1.5">
                            {res.status === 'passed' ? (
                              <CheckCircle2 size={14} className="text-emerald-600" />
                            ) : (
                              <XCircle size={14} className="text-rose-600" />
                            )}
                            Test Case #{res.test_case} {res.is_hidden ? '(Hidden Evaluation Case)' : ''}
                          </span>
                          <span className="text-[10px] font-mono text-slate-400">
                            {res.execution_time_ms}ms
                          </span>
                        </div>

                        {/* Console Stdout section if student printed anything */}
                        {res.stdout && (
                          <div className="space-y-1">
                            <span className="text-[10px] font-sans text-amber-500 font-bold block">
                              Console Output (Stdout):
                            </span>
                            <pre className="p-2.5 bg-slate-950 text-amber-300 border border-amber-900/40 rounded-xl font-mono text-xs overflow-x-auto whitespace-pre-wrap">
                              {res.stdout}
                            </pre>
                          </div>
                        )}

                        {/* Error traceback if runtime error */}
                        {res.error_message && (
                          <pre className="p-2.5 bg-rose-950 text-rose-300 border border-rose-900 rounded-xl font-mono text-xs overflow-x-auto whitespace-pre-wrap">
                            {res.error_message}
                          </pre>
                        )}

                        {/* Input & Output Comparison for non-hidden tests */}
                        {!res.is_hidden && (res.input || res.expected_output) && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 font-mono text-xs pt-1">
                            <div>
                              <span className="text-[10px] font-sans text-slate-500 dark:text-slate-400 font-bold block mb-1">
                                Your Output (Return Value)
                              </span>
                              <pre
                                className={`p-2.5 rounded-xl border font-mono text-xs overflow-x-auto ${
                                  res.status === 'passed'
                                    ? 'bg-slate-950 text-emerald-400 border-emerald-900/50'
                                    : 'bg-slate-950 text-rose-400 border-rose-900/50'
                                }`}
                              >
                                {res.actual_output !== undefined && res.actual_output !== ''
                                  ? res.actual_output
                                  : '<None / No Return Value>'}
                              </pre>
                            </div>
                            <div>
                              <span className="text-[10px] font-sans text-slate-500 dark:text-slate-400 font-bold block mb-1">
                                Expected Output
                              </span>
                              <pre className="p-2.5 bg-slate-950 text-emerald-400 border border-slate-800 rounded-xl font-mono text-xs overflow-x-auto">
                                {res.expected_output}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  )}

                  {!lastAction && (
                    <div className="text-center py-8 text-slate-400 font-semibold">
                      Click "Run Code" or "Submit" to see test case execution output.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Flow Navigation Footer */}
      <div className="bg-white dark:bg-slate-900 border border-border rounded-2xl p-4 shadow-soft flex items-center justify-between gap-3 shrink-0">
        <button
          type="button"
          onClick={onPrevious}
          onPointerDown={ripple.onPointerDown}
          className="ripple-trigger inline-flex items-center gap-1.5 px-4 py-2.5 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all cursor-pointer"
        >
          <ArrowLeft size={15} />
          Previous
        </button>

        <span className="text-[11px] font-semibold text-slate-400 hidden sm:inline">
          Problem {problemNumber} of {totalProblems}
        </span>

        <button
          type="button"
          onClick={onNext}
          onPointerDown={ripple.onPointerDown}
          className="ripple-trigger inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-black transition-all cursor-pointer shadow-sm"
        >
          {isLastProblem ? (
            <>
              <PartyPopper size={15} />
              Complete Week 1
            </>
          ) : (
            <>
              Next Problem
              <ArrowRight size={15} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CodingTaskPanel;
